import _taggedTemplateLiteral from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject;

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, useTheme } from 'styled-components';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { fetcher } from '@yoweb/utils/common';
import { RenderOn } from '@yoweb/ui/components/RenderOn/RenderOn';
import { YohanaLogo, AddCircleIcon, AddIcon, MenuIcon, CloseIcon, CaretRightIcon, CaretLeftIcon } from '@yoweb/ui/components/Icon';
import { th } from '@yoweb/ui/styles/utils/theme';
import { media } from '@yoweb/ui/styles/utils';
import { Box } from '@yoweb/ui/components/Box';
import { Button, ButtonIcon } from '@yoweb/ui/components/buttons';
import { Title, Text, Interactive } from '@yoweb/ui/components/typography';
import { useTranslation } from '@yoweb/next-i18next';
import { useUser } from '@poplar/hooks/useUser';
import { useTasks } from '@poplar/contexts/TaskContext/TaskProvider';
import { formatDate } from '@yoweb/utils/date';
import { API_LOGOUT, API_SUBSCRIPTION, API_TASK_COMPLETED_COUNT } from '@poplar/constants/routes';
import { convertTimeProtoToTimestamp } from '@yoweb/utils/converters';
import { useEntitlement } from '@poplar/contexts/EntitlementProvider';
import { AccessType } from '@poplar/types/enums';
import { personNameWithFallback } from '@poplar/utils/user';
import { logoutEvent } from '@yoweb/utils/analytics/mixpanel';
import { useExternalConfig } from '@poplar/contexts/ExternalConfig';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ACTIVE_SUBSCRIPTION_TYPES = [AccessType.FULL, AccessType.LIMITED];

var NavSmall = function NavSmall(_ref) {
  var _user$profile;

  var navItems = _ref.navItems,
      pageLabel = _ref.pageLabel;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t,
      i18n = _useTranslation.i18n;

  var theme = useTheme();
  var user = useUser();

  var _useExternalConfig = useExternalConfig(),
      mixpanel = _useExternalConfig.mixpanel;

  var _useRouter = useRouter(),
      pathname = _useRouter.pathname;

  var _useEntitlement = useEntitlement(),
      accessType = _useEntitlement.accessType;

  var _useState = useState(false),
      isFlyoutOpen = _useState[0],
      setIsFlyoutOpen = _useState[1];

  var _useState2 = useState(true),
      isAtTop = _useState2[0],
      setIsAtTop = _useState2[1];

  var _useState3 = useState('down'),
      scrollDirection = _useState3[0],
      setScrollDirection = _useState3[1];

  var _useState4 = useState(null),
      flyoutSubMenu = _useState4[0],
      setFlyoutSubMenu = _useState4[1];

  var _useState5 = useState(true),
      isInitialRender = _useState5[0],
      setIsInitialRender = _useState5[1];

  var hasActiveSubscription = ACTIVE_SUBSCRIPTION_TYPES.includes(accessType);

  var _useTasks = useTasks(),
      openTaskIntakeModal = _useTasks.actions.openTaskIntakeModal;

  var _useSWR = useSWR(hasActiveSubscription ? API_SUBSCRIPTION : undefined, fetcher, {
    shouldRetryOnError: false,
    revalidateOnFocus: false
  }),
      subscription = _useSWR.data;

  var _useSWR2 = useSWR(API_TASK_COMPLETED_COUNT, fetcher, {
    fallbackData: 0,
    refreshInterval: 1000 * 60 * 5,
    // 5 mins
    shouldRetryOnError: false,
    revalidateOnFocus: false
  }),
      completedTaskCount = _useSWR2.data;

  var lastScrollPos = useRef(0);
  var subscriptionStartDate = convertTimeProtoToTimestamp(subscription === null || subscription === void 0 ? void 0 : subscription.startDate);
  var flyoutWidth = theme.sizes.sideNavFlyoutWidth;
  useEffect(function () {
    if (isInitialRender) {
      setIsInitialRender(false);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);
  useEffect(function () {
    if (isFlyoutOpen) {
      setFlyoutSubMenu(null);
    }
  }, [isFlyoutOpen]); // Detect scroll position and set a value when at top

  useEffect(function () {
    var handleScroll = function handleScroll() {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener('scroll', handleScroll);
    return function () {
      return window.removeEventListener('scroll', handleScroll);
    };
  }); // Detect scroll direction

  useEffect(function () {
    var handleScroll = function handleScroll() {
      var currentScrollPos = window.scrollY;

      if (currentScrollPos > lastScrollPos.current) {
        setScrollDirection('down');
      } else {
        setScrollDirection('up');
      }

      lastScrollPos.current = currentScrollPos;
    };

    window.addEventListener('scroll', handleScroll);
    return function () {
      return window.removeEventListener('scroll', handleScroll);
    };
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(NavbarContainer, {
      $backgroundTransparent: isAtTop,
      $isHidden: scrollDirection === 'down' && !isAtTop,
      "data-testid": "poplar-nav",
      children: [/*#__PURE__*/_jsx(Box, {
        justifySelf: "start",
        onClick: function onClick() {
          return setIsFlyoutOpen(true);
        },
        "data-testid": "nav-open-flyout",
        children: /*#__PURE__*/_jsx(MenuIcon, {
          size: 24
        })
      }), pageLabel ? /*#__PURE__*/_jsx(Caption, {
        children: pageLabel
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(RenderOn.MobileOnly, {
          children: /*#__PURE__*/_jsx(YohanaLogo, {
            width: 75,
            height: "100%"
          })
        }), /*#__PURE__*/_jsx(RenderOn.TabletOnly, {
          children: /*#__PURE__*/_jsx(YohanaLogo, {
            width: 95,
            height: "100%"
          })
        })]
      }), /*#__PURE__*/_jsxs(Box, {
        justifySelf: "end",
        children: [/*#__PURE__*/_jsx(RenderOn.TabletOnly, {
          children: /*#__PURE__*/_jsx(Button, {
            iconLeft: /*#__PURE__*/_jsx(AddCircleIcon, {
              size: 14
            }),
            size: "md",
            onClick: function onClick() {
              return openTaskIntakeModal();
            },
            children: t('actions.add-todo')
          })
        }), /*#__PURE__*/_jsx(RenderOn.MobileOnly, {
          children: /*#__PURE__*/_jsx(ButtonIcon, {
            size: "sm",
            icon: /*#__PURE__*/_jsx(AddIcon, {
              size: 16
            }),
            onClick: function onClick() {
              return openTaskIntakeModal();
            }
          })
        })]
      })]
    }), !isInitialRender && /*#__PURE__*/createPortal( /*#__PURE__*/_jsxs(Outter, {
      children: [/*#__PURE__*/_jsx(PageCover, {
        initial: {
          opacity: 0
        },
        animate: {
          opacity: isFlyoutOpen ? 0.5 : 0
        },
        transition: {
          type: 'ease-in-out',
          duration: 0.3
        },
        onClick: function onClick() {
          return setIsFlyoutOpen(false);
        },
        "data-testid": "nav-page-cover",
        $enableEvents: isFlyoutOpen,
        role: "presentation"
      }), /*#__PURE__*/_jsx(StyledFlyout, {
        initial: {
          x: "-".concat(flyoutWidth)
        },
        animate: {
          x: isFlyoutOpen ? 0 : "-".concat(flyoutWidth)
        },
        transition: {
          type: 'ease-in-out',
          duration: 0.3
        },
        "data-testid": "nav-flyout",
        children: flyoutSubMenu ? /*#__PURE__*/_jsxs(FlyoutSubMenuContent, {
          children: [/*#__PURE__*/_jsxs(SubActionRow, {
            children: [/*#__PURE__*/_jsx(SubActionButton, {
              onClick: function onClick() {
                return setFlyoutSubMenu(null);
              },
              "data-testid": "nav-sub-back",
              children: /*#__PURE__*/_jsx(CaretLeftIcon, {
                size: 20
              })
            }), /*#__PURE__*/_jsx(SubActionButton, {
              onClick: function onClick() {
                return setIsFlyoutOpen(false);
              },
              "data-testid": "nav-sub-close",
              children: /*#__PURE__*/_jsx(CloseIcon, {
                size: 20
              })
            })]
          }), /*#__PURE__*/_jsx(SubNavItems, {
            role: "navigation",
            children: flyoutSubMenu.map(function (item) {
              var _item$activePatterns;

              var isActive = (_item$activePatterns = item.activePatterns) === null || _item$activePatterns === void 0 ? void 0 : _item$activePatterns.some(function (pattern) {
                return pathname.match(pattern);
              });
              if (item.largeOnly) return null;

              if ('url' in item) {
                return /*#__PURE__*/_jsx(Link, {
                  href: item.url,
                  passHref: true,
                  legacyBehavior: true,
                  children: /*#__PURE__*/_jsxs(NavRow, {
                    as: "a",
                    target: item.target,
                    "data-testid": "".concat(item.testId, "-small"),
                    onClick: function onClick() {
                      return setIsFlyoutOpen(false);
                    },
                    role: "link",
                    tabIndex: 0,
                    $isActive: isActive,
                    children: [/*#__PURE__*/_jsx("span", {
                      children: item.icon
                    }), /*#__PURE__*/_jsx(Interactive, {
                      size: "xl",
                      as: "span",
                      children: item.name
                    })]
                  })
                }, item.name);
              }

              if ('action' in item) {
                return /*#__PURE__*/_jsxs(NavRow, {
                  onClick: item.action,
                  "data-testid": "".concat(item.testId, "-small"),
                  children: [/*#__PURE__*/_jsx("span", {
                    children: item.icon
                  }), /*#__PURE__*/_jsx(Interactive, {
                    size: "xl",
                    as: "span",
                    children: item.name
                  })]
                }, item.name);
              }
            })
          }), /*#__PURE__*/_jsx(SignOutSection, {
            children: /*#__PURE__*/_jsx(Link, {
              href: API_LOGOUT,
              prefetch: false,
              passHref: true,
              legacyBehavior: true,
              children: /*#__PURE__*/_jsx(Interactive, {
                size: "md",
                as: "a",
                "data-testid": "nav-sign-out-small-button",
                onClick: function onClick() {
                  logoutEvent(mixpanel.domain);
                },
                children: t('sign-out')
              })
            })
          })]
        }) : /*#__PURE__*/_jsxs(FlyoutContent, {
          children: [/*#__PURE__*/_jsx(ActionRow, {
            children: /*#__PURE__*/_jsx(SubActionButton, {
              onClick: function onClick() {
                return setIsFlyoutOpen(false);
              },
              children: /*#__PURE__*/_jsx(CloseIcon, {
                size: 20,
                "data-testid": "nav-flyout-close"
              })
            })
          }), /*#__PURE__*/_jsxs(UserSection, {
            children: [/*#__PURE__*/_jsx(Title, {
              size: "md",
              as: "p",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              children: t('navGlobal.user-greeting', {
                name: personNameWithFallback(user === null || user === void 0 ? void 0 : (_user$profile = user.profile) === null || _user$profile === void 0 ? void 0 : _user$profile.personName)
              })
            }), /*#__PURE__*/_jsxs("div", {
              children: [subscriptionStartDate && /*#__PURE__*/_jsx(Text, {
                size: "md",
                "data-testid": "nav-flyout-member-since",
                children: t('navGlobal.member-since', {
                  date: formatDate(subscriptionStartDate, {
                    locale: i18n.language,
                    timeZone: user.timeZone,
                    dateStyle: 'long'
                  })
                })
              }), /*#__PURE__*/_jsx(Text, {
                size: "md",
                children: t('navGlobal.todo-completed-count', {
                  count: completedTaskCount
                })
              })]
            })]
          }), /*#__PURE__*/_jsx(NavItems, {
            role: "navigation",
            children: navItems.map(function (item) {
              if ('nested' in item) {
                var _item$activePatterns2;

                return /*#__PURE__*/_jsxs(NavRow, {
                  onClick: function onClick() {
                    return setFlyoutSubMenu(item.nested);
                  },
                  "data-testid": "".concat(item.testId, "-small"),
                  tabIndex: 0,
                  $isActive: (_item$activePatterns2 = item.activePatterns) === null || _item$activePatterns2 === void 0 ? void 0 : _item$activePatterns2.some(function (pattern) {
                    return pathname.match(pattern);
                  }),
                  children: [/*#__PURE__*/_jsx("span", {
                    children: item.icon
                  }), /*#__PURE__*/_jsx(Interactive, {
                    size: "xl",
                    as: "span",
                    children: item.name
                  }), /*#__PURE__*/_jsx("span", {
                    children: /*#__PURE__*/_jsx(CaretRightIcon, {
                      size: 16
                    })
                  })]
                }, item.name);
              }

              if ('url' in item) {
                var _item$activePatterns3;

                var isActive = (_item$activePatterns3 = item.activePatterns) === null || _item$activePatterns3 === void 0 ? void 0 : _item$activePatterns3.some(function (pattern) {
                  return pathname.match(pattern);
                });
                return /*#__PURE__*/_jsx(Link, {
                  href: item.url,
                  passHref: true,
                  legacyBehavior: true,
                  children: /*#__PURE__*/_jsxs(NavRow, {
                    as: "a",
                    target: item.target,
                    "data-testid": "".concat(item.testId, "-small"),
                    onClick: function onClick() {
                      return setIsFlyoutOpen(false);
                    },
                    role: "link",
                    tabIndex: 0,
                    $isActive: isActive,
                    children: [/*#__PURE__*/_jsx("span", {
                      children: isActive ? item.iconActive : item.icon
                    }), /*#__PURE__*/_jsx(Interactive, {
                      size: "xl",
                      as: "span",
                      children: item.name
                    })]
                  })
                }, item.name);
              }
            })
          }), /*#__PURE__*/_jsx(Box, {
            mt: "normal3",
            mx: "normal2",
            children: /*#__PURE__*/_jsx(Button, {
              size: "lg",
              isFullWidth: true,
              iconLeft: /*#__PURE__*/_jsx(AddCircleIcon, {
                size: 20
              }),
              onClick: function onClick() {
                setIsFlyoutOpen(false);
                openTaskIntakeModal();
              },
              children: t('actions.add-todo')
            })
          }), /*#__PURE__*/_jsx(SignOutSection, {
            children: /*#__PURE__*/_jsx(Link, {
              href: API_LOGOUT,
              prefetch: false,
              passHref: true,
              legacyBehavior: true,
              children: /*#__PURE__*/_jsx(Interactive, {
                size: "md",
                as: "a",
                "data-testid": "nav-sign-out-small-button",
                onClick: function onClick() {
                  logoutEvent(mixpanel.domain);
                },
                children: t('sign-out')
              })
            })
          })]
        })
      })]
    }), document.body)]
  });
};

var NavRow = styled.button.withConfig({
  componentId: "sc-1nk1t03-0"
})(["display:grid;grid-template-columns:", " 1fr ", ";place-items:center start;width:100%;padding:", ";gap:", ";min-height:", ";border:none;border-radius:", ";background-color:", ";&:hover{background-color:", ";}&:first-child{border-top:none;}"], th.getSpace('normal2'), th.getSpace('normal2'), th.getSpace('small4'), th.getSpace('small4'), th.getSpace('medium2'), th.getRadii('small'), function (_ref2) {
  var $isActive = _ref2.$isActive;
  return $isActive ? th.getColor('sandLight') : 'transparent';
}, th.getColor('base050'));
var Caption = styled.h3.withConfig({
  componentId: "sc-1nk1t03-1"
})(["font-size:", ";font-weight:", ";text-align:center;"], th.getFontSize('labelSm'), th.getFontWeight('semibold'));
var ActionRow = styled.div.withConfig({
  componentId: "sc-1nk1t03-2"
})(["display:flex;justify-content:flex-end;padding:", ";width:100%;"], th.getSpace('normal1'));
var SubActionRow = styled(ActionRow).withConfig({
  componentId: "sc-1nk1t03-3"
})(["&&{justify-content:space-between;}"]);
var SubActionButton = styled.button.withConfig({
  componentId: "sc-1nk1t03-4"
})(["align-items:center;background-color:transparent;border:none;padding:", ";"], th.getSpace('small3'));
var UserSection = styled.div.withConfig({
  componentId: "sc-1nk1t03-5"
})(["display:grid;gap:", ";margin-left:", ";margin-right:", ";"], th.getSpace('normal1'), th.getSpace('normal2'), th.getSpace('normal2'));
var NavItems = styled.div.withConfig({
  componentId: "sc-1nk1t03-6"
})(["margin-top:", ";margin-left:", ";margin-right:", ";"], th.getSpace('normal2'), th.getSpace('normal2'), th.getSpace('normal2'));
var SubNavItems = styled.div.withConfig({
  componentId: "sc-1nk1t03-7"
})(["margin-top:", ";margin-left:clamp(0px,", ",", ");margin-right:clamp(0px,", ",", ");"], th.getSpace('small2'), th.getSpace('normal3'), th.getSpace('normal3'), th.getSpace('normal3'), th.getSpace('normal3'));
var SignOutSection = styled.div.withConfig({
  componentId: "sc-1nk1t03-8"
})(["margin-left:", ";margin-right:", ";margin-bottom:", ";align-self:end;"], th.getSpace('normal2'), th.getSpace('normal2'), th.getSpace('normal4'));
var NavbarContainer = styled.div.withConfig({
  componentId: "sc-1nk1t03-9"
})(["position:fixed;display:grid;grid-template-columns:75px 1fr 75px;place-items:center center;top:0;left:0;right:0;height:", ";padding:", " ", ";background-color:", ";transition:background-color 300ms linear,transform 300ms ease-in-out;z-index:", ";transform:translateY(0);", " ", " ", ""], th.getSpace('medium2'), th.getSpace('small4'), th.getSpace('normal1'), th.getColor('base000'), th.getZIndex('popover'), media.md(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    height: ", ";\n    grid-template-columns: 175px 1fr 175px;\n  "])), th.getSpace('xl1')), function (_ref3) {
  var $backgroundTransparent = _ref3.$backgroundTransparent;
  return $backgroundTransparent && css(["background-color:transparent;"]);
}, function (_ref4) {
  var $isHidden = _ref4.$isHidden;
  return $isHidden && css(["transform:translateY(-100%);"]);
});
var Outter = styled.div.withConfig({
  componentId: "sc-1nk1t03-10"
})(["position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:", ";"], th.getZIndex('modal'));
var StyledFlyout = styled(motion.div).withConfig({
  componentId: "sc-1nk1t03-11"
})(["position:fixed;left:0;top:0;bottom:0;pointer-events:all;z-index:", ";width:100%;max-width:min(", ",100%);background-color:", ";"], th.getZIndex('modal'), th.getSize('sideNavFlyoutWidth'), th.getColor('base000'));
var PageCover = styled(motion.div).withConfig({
  componentId: "sc-1nk1t03-12"
})(["position:absolute;inset:0;background-color:", ";z-index:", ";pointer-events:", ";"], th.getColor('charcoal'), th.getZIndex('popover'), function (_ref5) {
  var $enableEvents = _ref5.$enableEvents;
  return $enableEvents ? 'all' : 'none';
});
var FlyoutContent = styled.div.withConfig({
  componentId: "sc-1nk1t03-13"
})(["position:relative;overflow:hidden auto;height:100%;display:grid;grid-template-rows:auto auto auto auto 1fr;"]);
var FlyoutSubMenuContent = styled.div.withConfig({
  componentId: "sc-1nk1t03-14"
})(["position:relative;overflow:hidden auto;height:100%;display:grid;grid-template-rows:auto auto 1fr;"]);
export { NavSmall };
export default NavSmall;